import { useEffect, useState } from "react";
import { Package, Plus, Minus, Trash2, Pencil, X, Loader2, AlertTriangle, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", unit: "pièces", quantity: 0, min_threshold: 2 };

export const StockAdmin = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get("/admin/stock").then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const low = (items || []).filter((i) => i.low);

  const adjust = async (item, delta) => {
    setBusyId(item.id);
    try {
      await api.post(`/admin/stock/${item.id}/adjust`, null, { params: { delta } });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const save = async () => {
    try {
      const payload = { ...editing, quantity: parseInt(editing.quantity) || 0, min_threshold: parseInt(editing.min_threshold) || 0 };
      if (editing.id) {
        await api.put(`/admin/stock/${editing.id}`, payload);
      } else {
        await api.post("/admin/stock", payload);
      }
      toast.success("Article enregistré");
      setEditing(null);
      setAdding(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/stock/${id}`);
    toast.success("Article supprimé");
    load();
  };

  const inputCls =
    "bg-white border border-taupe/30 rounded-md px-3.5 py-2.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300 w-full";

  return (
    <div className="space-y-6" data-testid="admin-stock-tab">
      {items && low.length > 0 && (
        <div className="bg-[#9E4747]/8 border border-[#9E4747]/35 rounded-2xl p-5 flex items-start gap-4" data-testid="stock-alert-banner">
          <AlertTriangle className="w-5 h-5 text-[#9E4747] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-sm font-medium text-[#9E4747]">
              {low.length} article{low.length > 1 ? "s" : ""} en stock faible
            </p>
            <p className="text-sm text-espresso/65 font-light mt-1">
              {low.map((i) => i.name).join(", ")} — pensez à commander.
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-espresso/60 font-light">Suivez votre matériel. Un article devient rouge quand son stock atteint le minimum.</p>
        <button
          data-testid="stock-add-btn"
          onClick={() => { setEditing({ ...EMPTY }); setAdding(true); }}
          className="rounded-full bg-espresso text-cream px-5 py-2.5 text-xs tracking-[0.1em] uppercase inline-flex items-center gap-2 hover:bg-taupe transition-colors duration-300"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>

      {editing && (
        <div className="bg-cream border border-taupe/30 rounded-2xl p-6 space-y-4" data-testid="stock-editor">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-xl text-espresso">{editing.id ? "Modifier" : "Nouvel article"}</h3>
            <button onClick={() => { setEditing(null); setAdding(false); }} data-testid="stock-editor-close" className="text-espresso/40 hover:text-espresso">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <input className={inputCls} data-testid="stock-name-input" placeholder="Nom (ex : Gel laser)" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <input className={inputCls} data-testid="stock-unit-input" placeholder="Unité (ex : flacons)" value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} />
            <input className={inputCls} data-testid="stock-qty-input" type="number" min="0" placeholder="Quantité" value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} />
            <input className={inputCls} data-testid="stock-min-input" type="number" min="0" placeholder="Alerte sous…" value={editing.min_threshold} onChange={(e) => setEditing({ ...editing, min_threshold: e.target.value })} />
          </div>
          <button
            data-testid="stock-save-btn"
            onClick={save}
            disabled={!editing.name.trim()}
            className="rounded-full bg-taupe text-cream px-6 py-3 text-xs tracking-[0.1em] uppercase hover:bg-taupe-dark transition-colors duration-300 disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      )}

      {!items ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-espresso/45" data-testid="stock-empty">
          <Package className="w-8 h-8 mx-auto mb-3 text-taupe/50" strokeWidth={1.5} />
          <p className="text-sm">Aucun article. Ajoutez votre premier matériel.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="stock-list">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`stock-item-${item.id}`}
              className={`border rounded-2xl px-5 py-4 flex items-center justify-between gap-4 transition-colors duration-300 ${
                item.low ? "bg-[#9E4747]/8 border-[#9E4747]/40" : "bg-white border-taupe/20"
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-espresso flex items-center gap-2">
                  {item.name}
                  {item.low && (
                    <span className="rounded-full bg-[#9E4747] text-white px-2.5 py-0.5 text-[10px] tracking-[0.08em] uppercase" data-testid={`stock-low-${item.id}`}>
                      Stock faible
                    </span>
                  )}
                </p>
                <p className="text-xs text-espresso/50 mt-0.5">Alerte sous {item.min_threshold} {item.unit}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid={`stock-minus-${item.id}`}
                  disabled={busyId === item.id || item.quantity === 0}
                  onClick={() => adjust(item, -1)}
                  className="w-8 h-8 rounded-full border border-taupe/30 flex items-center justify-center text-espresso/60 hover:border-taupe hover:text-espresso transition-colors duration-300 disabled:opacity-30"
                >
                  <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <span className="w-16 text-center" data-testid={`stock-qty-${item.id}`}>
                  <span className={`font-display text-2xl ${item.low ? "text-[#9E4747]" : "text-espresso"}`}>{item.quantity}</span>
                  <span className="block text-[10px] text-espresso/45 leading-none">{item.unit}</span>
                </span>
                <button
                  data-testid={`stock-plus-${item.id}`}
                  disabled={busyId === item.id}
                  onClick={() => adjust(item, 1)}
                  className="w-8 h-8 rounded-full border border-taupe/30 flex items-center justify-center text-espresso/60 hover:border-taupe hover:text-espresso transition-colors duration-300 disabled:opacity-30"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <button data-testid={`stock-edit-${item.id}`} onClick={() => { setEditing({ ...item }); setAdding(false); }} className="text-espresso/40 hover:text-espresso transition-colors duration-300 ml-1">
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button data-testid={`stock-delete-${item.id}`} onClick={() => remove(item.id)} className="text-espresso/40 hover:text-[#9E4747] transition-colors duration-300">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {items && items.length > 0 && low.length === 0 && (
        <p className="text-sm text-espresso/50 flex items-center gap-2" data-testid="stock-all-good">
          <PackageCheck className="w-4 h-4 text-[#4A6B53]" strokeWidth={1.5} /> Tous les stocks sont au-dessus du minimum.
        </p>
      )}
    </div>
  );
};
